// Campaign map definitions. Each map is a self-contained scenario with its
// own grid, biome palette, waypoints, wave script, and modifiers.
//
// `MapData.fromDef` (in MapData.ts) consumes these to build the runtime grid.
// Adding a new map = append a MapDef here, then register it in CAMPAIGN_MAPS.

import { ENEMIES } from "../config";
import { getThemeDef, type EnemyKey } from "../systems/waveGeneration";
import type { FoliageSpot, FoliageArea, FoliageZone } from "../systems/FoliageOcclusions";

export type Biome = "plains" | "river" | "swamp" | "snow" | "mine" | "desert" | "siege";

type SpawnSpec = { type: string; count: number; interval: number };

/** Total base HP of a spawn list — the yardstick filler waves are sized against. */
function waveHp(entries: SpawnSpec[]): number {
  let hp = 0;
  for (const e of entries) hp += (ENEMIES[e.type]?.hp ?? 0) * e.count;
  return hp;
}

/**
 * Campaign maps are hand-authored up to a point, then filled out to
 * `targetCount` waves.
 *
 * The filler continues *this map's* curve instead of restarting a generic one:
 * it starts from the strength and payout of the last authored wave, and draws
 * only from enemies the map already fields (plus its biome pair), so two maps
 * that share a biome do not end up with the same late game. The boss round is
 * sized to outweigh the wave before it, so the campaign peaks on its finale.
 */
function buildCampaignWaves(
  handWaves: WaveSpec[],
  targetCount: number,
  biome: Biome,
  finalBoss: { type: EnemyKey; escort: SpawnSpec[] }
): WaveSpec[] {
  const waves: WaveSpec[] = handWaves.map(w => ({
    reward: w.reward,
    boss: w.boss,
    entries: w.entries.map(e => ({ type: e.type, count: e.count, interval: e.interval }))
  }));

  // The map's roster is its identity: every enemy its authored waves use, in
  // first-appearance order, plus the biome's own skirmisher/elite pair.
  const themed = getThemeDef(biome);
  const roster: string[] = [];
  for (const type of [...handWaves.flatMap(w => w.entries.map(e => e.type)), themed.skirmisher, themed.elite]) {
    if (!roster.includes(type) && ENEMIES[type] && !ENEMIES[type].boss) roster.push(type);
  }

  // Cheap fodder shows up in packs, heavies in handfuls — keeps a filler wave
  // reading like a wave instead of an even split across the roster.
  const packSize = (type: string): number => {
    const hp = ENEMIES[type].hp;
    return hp < 80 ? 2.5 : hp < 200 ? 1.4 : 0.6;
  };

  const tail = handWaves[handWaves.length - 1];
  const baseHp = waveHp(tail.entries);
  const fillerCount = targetCount - 1 - handWaves.length;

  for (let i = 1; i <= fillerCount; i++) {
    const t = fillerCount > 1 ? (i - 1) / (fillerCount - 1) : 0;   // 0..1 across the stretch

    // Widen the mix as the map goes on, and slide the window each wave so no
    // two consecutive waves field the same line-up.
    const width = Math.min(roster.length, 3 + Math.round(t * (roster.length - 3)));
    const mix: string[] = [];
    for (let k = 0; k < width; k++) {
      const type = roster[(i * 2 + k) % roster.length];
      if (!mix.includes(type)) mix.push(type);
    }

    const hpTarget = baseHp * (1.1 + t * 2.9);
    let unit = 0;
    for (const type of mix) unit += ENEMIES[type].hp * packSize(type);
    const squad = hpTarget / unit;

    const entries: SpawnSpec[] = mix.map(type => ({
      type,
      count: Math.max(1, Math.round(squad * packSize(type))),
      interval: 0
    }));

    // WaveManager lays spawns end to end across all entries, so the interval
    // has to come from a wave-length budget — fixed intervals stretch a big
    // late wave past a minute. The 180ms floor keeps the spawn rate under
    // roughly six enemies a second no matter how big the wave gets.
    const spawnMs = 16_000 + t * 12_000;
    const total = entries.reduce((n, e) => n + e.count, 0);
    for (const e of entries) e.interval = Math.max(180, Math.round(spawnMs / total));

    waves.push({ reward: Math.round(tail.reward * (1.08 + t * 1.9)), entries });
  }

  // The finale has to be the peak. Scale the authored escort until the boss
  // round outweighs the wave before it.
  const lastHp = waveHp(waves[waves.length - 1].entries);
  const escortHp = waveHp(finalBoss.escort);
  const gap = lastHp * 1.25 - ENEMIES[finalBoss.type].hp;
  const scale = escortHp > 0 ? Math.min(6, Math.max(0.8, gap / escortHp)) : 1;

  waves.push({
    reward: Math.round(tail.reward * 3.4),
    boss: true,
    entries: [
      { type: finalBoss.type, count: 1, interval: 0 },
      // Intervals shrink with the count so a bigger escort arrives as denser
      // pressure rather than a longer wave.
      ...finalBoss.escort.map(e => ({
        type: e.type,
        count: Math.max(1, Math.round(e.count * scale)),
        interval: Math.max(150, Math.round(e.interval / scale))
      }))
    ]
  });

  return waves;
}
export interface BiomePalette {
  grassA: number;       // legacy checkerboard light (kept as a fallback)
  grassB: number;       // legacy checkerboard dark
  /** Per-cell randomized grass tints (4 shades). MapRenderer picks one
   *  deterministically per (mapKey, c, r) so each map keeps a stable look. */
  grassShades: number[];
  pathA: number;
  pathB: number;
  rockColor: number;
  ambient: number;      // overall scene tint (0xRRGGBB or 0xffffff for none)
}

export const BIOME_PALETTES: Record<Biome, BiomePalette> = {
  // Warm hand-painted forest: olive-gold grass, deep pine shadows, sandy paths.
  plains: {
    grassA: 0x6f8a3a, grassB: 0x556e2c,
    grassShades: [0x7f9a44, 0x6f8a36, 0x5f7a2c, 0x88a046],
    pathA: 0xb89a5e, pathB: 0x9a7c44, rockColor: 0x6e6658, ambient: 0xfff4d8
  },
  // River map: greener banks with a hint of moss + cooler ambient.
  river:  {
    grassA: 0x6a8a36, grassB: 0x506e28,
    grassShades: [0x7a9a3c, 0x648a32, 0x4f6e26, 0x82a040],
    pathA: 0xa8895a, pathB: 0x886a3c, rockColor: 0x6c6e72, ambient: 0xeaf2ff
  },
  swamp:  {
    grassA: 0x3a5a2a, grassB: 0x2a4020,
    grassShades: [0x3a5a2a, 0x2a4020, 0x4a6a2c, 0x32502a],
    pathA: 0x5a4a30, pathB: 0x4a3a20, rockColor: 0x4a4a3a, ambient: 0xc0d0a0
  },
  // Snow: subtle blue/white shifts so the snowfield isn't a flat sheet.
  snow:   {
    grassA: 0xe2ecf4, grassB: 0xc4d4e2,
    grassShades: [0xeaf2f8, 0xd4e0ea, 0xbccada, 0xf4f8fc],
    pathA: 0x7a7686, pathB: 0x60596f, rockColor: 0x8a93a3, ambient: 0xeef6ff
  },
  mine:   {
    grassA: 0x3a3028, grassB: 0x2a221a,
    grassShades: [0x3a3028, 0x2a221a, 0x453a30, 0x342a22],
    pathA: 0x5a4a3a, pathB: 0x4a3a2a, rockColor: 0x6a5a4a, ambient: 0xb0a090
  },
  desert: {
    grassA: 0xc8a868, grassB: 0xa88848,
    grassShades: [0xd0b070, 0xb89a58, 0xa88848, 0xdcb878],
    pathA: 0x806030, pathB: 0x705028, rockColor: 0x9a8060, ambient: 0xfff0c0
  },
  siege:  {
    grassA: 0x4a4030, grassB: 0x3a3024,
    grassShades: [0x4a4030, 0x3a3024, 0x55482e, 0x32281e],
    pathA: 0x6a4a30, pathB: 0x5a3a20, rockColor: 0x504848, ambient: 0xf0d8b0
  }
};

export interface WaveSpec {
  reward: number;
  /** Each entry spawns `count` of `type` separated by `interval`ms. */
  entries: { type: string; count: number; interval: number }[];
  /** Marks this wave as a boss wave for UI emphasis. */
  boss?: boolean;
}

export type MapModifier =
  | { kind: "blizzard"; everyMs: number; durMs: number; rofMult: number }
  | { kind: "sandstorm"; everyMs: number; durMs: number }
  | { kind: "fog"; rangePct: number }; // permanent passive range reduction

export interface MapDef {
  key: string;
  name: string;
  biome: Biome;
  /** Waypoints in grid coords. Negative col/row = off-screen spawn. */
  waypoints: [number, number][];
  /** Optional second path (river maps etc.). Same coordinate convention. */
  altWaypoints?: [number, number][];
  /**
   * Road centreline authored against the 768x432 painted background and stored
   * as normalized coordinates. When present it replaces the coarse tile route
   * for enemy movement and path-cell blocking.
   */
  paintedWaypoints?: [number, number][];
  /** Optional second painted lane. Its final point must match the merge point on the main lane. */
  altPaintedWaypoints?: [number, number][];
  /** Logical half-width of the non-buildable road corridor in gameplay pixels. */
  pathBlockRadius?: number;
  /** Authored background aspect ratio. Defaults to 16:9. */
  backgroundAspect?: number;
  /** Decorative grass tiles to convert to rock/torch/etc. (col, row, tileKind). */
  decorations?: { c: number; r: number; kind: "rock" | "fountain" | "torch" | "banner" | "pine" | "water" | "ice_shard" }[];
  /** Tiles forced un-buildable (e.g. river banks). */
  blocked?: [number, number][];
  /** Painted-route cells that should remain buildable grass (col, row). */
  buildable?: [number, number][];
  castleHp: number;
  startGold: number;
  waves: WaveSpec[];
  modifiers: MapModifier[];
  /** Index in `waves` that is the final boss wave. */
  bossWave: number;
  description: string;
  /**
   * Optional pre-rendered background. When set AND the texture is loaded,
   * MapRenderer paints this image across the full canvas and skips per-tile
   * grass/path/decoration rendering. Tile data still drives game logic
   * (path = walkable for enemies, grass = buildable for towers, blocked =
   * un-buildable). Falls back to the procedural look if the texture is
   * missing.
   */
  backgroundImage?: string;
  /**
   * Circles (paintedSpot) or free-form polygons (paintedArea) authored in the
   * same 768x432 painted-pixel space as paintedWaypoints, where the painted art
   * is redrawn above towers and enemies so units there appear to stand behind
   * painted foliage. Only effective on maps with `backgroundImage`.
   */
  foliageOcclusions?: FoliageZone[];
}

const PAINTED_MAP_WIDTH = 768;
const PAINTED_MAP_HEIGHT = 432;

/** Keep route reviews readable by authoring points in source-image pixels. */
function paintedPath(...points: [number, number][]): [number, number][] {
  return points.map(([x, y]) => [x / PAINTED_MAP_WIDTH, y / PAINTED_MAP_HEIGHT]);
}

/** Author a foliage-occlusion circle in source-image pixels (matches paintedPath's convention). */
function paintedSpot(x: number, y: number, r: number): FoliageSpot {
  return { x: x / PAINTED_MAP_WIDTH, y: y / PAINTED_MAP_HEIGHT, r: r / PAINTED_MAP_WIDTH };
}

/** Author a free-form foliage-occlusion polygon in source-image pixels. */
function paintedArea(...points: [number, number][]): FoliageArea {
  return { points: points.map(([x, y]) => [x / PAINTED_MAP_WIDTH, y / PAINTED_MAP_HEIGHT]) };
}

// -----------------------------------------------------------------------------
// MAP 1 - Greenwold Pass (tutorial, single winding path)
// Backed by a hand-painted background (public/maps/greenwold.png). Waypoints
// trace the road in the painting, and the castle footprint on the right is
// flagged un-buildable so towers can't sit on top of the painted keep.
// -----------------------------------------------------------------------------
const GREENWOLD: MapDef = {
  key: "greenwold_pass",
  name: "Greenwold Pass",
  biome: "plains",
  // Smooth S that matches the painted road on map-greenwold.png:
  // enters top-left at row 1, sweeps down through the middle, then bends
  // east into the castle gate. Four corners give early players obvious
  // tower spots without overwhelming them with branches.
  waypoints: [
    [-1, 1], [5, 1], [5, 4], [11, 4], [11, 6], [16, 6]
  ],
  // Centreline measured off the actual painting (road pixel scan of
  // ui/map-previews/greenwold_pass.png): the road hugs the top-left edge,
  // runs east at y~105-112, drops at x~290, runs east again at y~215-222,
  // steps down at x~530 and ends at the castle gate around (740, 234).
  paintedWaypoints: paintedPath(
    [-24, 52], [30, 93], [70, 101], [130, 105], [200, 109], [255, 112],
    [278, 126], [290, 158], [296, 190], [305, 208],
    [350, 218], [420, 220], [470, 219], [505, 222],
    [524, 234], [532, 250],
    [560, 257], [600, 255], [640, 250], [672, 244], [700, 240], [740, 234]
  ),
  pathBlockRadius: 52,
  // These two clearings sit above the first bend. The broad painted-road
  // collision radius catches them, but the artwork clearly leaves room for a
  // tower on both cells.
  buildable: [[5, 2], [6, 2]],
  // Painted castle occupies the top-right corner (cols 16-19, rows 0-4) with
  // its wall continuing below the gate on col 19. Block so towers can't sit
  // on the keep or walls. Col 13 rows 6-8 cover the decorative southern
  // road fork so towers can't be placed on painted road either.
  blocked: [
    [16, 0], [17, 0], [18, 0], [19, 0],
    [16, 1], [17, 1], [18, 1], [19, 1],
    [16, 2], [17, 2], [18, 2], [19, 2],
    [16, 3], [17, 3], [18, 3], [19, 3],
    [16, 4], [17, 4], [18, 4], [19, 4],
    [19, 5], [19, 6],
    [13, 6], [13, 7], [13, 8]
  ],
  // No procedural decorations: the painting already has banners, torches,
  // pines, and rocks baked in.
  castleHp: 20,
  startGold: 250,
  description: "A wide forest road. Hold the line.",
  bossWave: 29,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 50,  entries: [{ type: "goblin", count: 8,  interval: 700 }] },
    { reward: 60,  entries: [{ type: "goblin", count: 12, interval: 550 }] },
    { reward: 75,  entries: [{ type: "goblin", count: 8,  interval: 500 }, { type: "skeleton", count: 4, interval: 700 }] },
    { reward: 90,  entries: [{ type: "skeleton", count: 11, interval: 520 }] },
    { reward: 110, entries: [{ type: "orc", count: 8, interval: 800 }] },
    { reward: 120, entries: [{ type: "swarmling", count: 24, interval: 180 }] },
    { reward: 130, entries: [{ type: "goblin", count: 20, interval: 300 }, { type: "orc", count: 5, interval: 1000 }] },
    { reward: 150, entries: [{ type: "wraith", count: 6, interval: 600 }, { type: "skeleton", count: 10, interval: 400 }] },
    { reward: 170, entries: [{ type: "flame_imp", count: 8, interval: 500 }, { type: "goblin", count: 12, interval: 350 }] },
    { reward: 200, entries: [{ type: "armored_orc", count: 5, interval: 900 }, { type: "orc", count: 8, interval: 600 }] },
    { reward: 220, entries: [{ type: "troll", count: 2, interval: 2000 }, { type: "orc", count: 10, interval: 600 }] },
    { reward: 280, entries: [{ type: "troll", count: 4, interval: 1500 }, { type: "skeleton", count: 25, interval: 250 }] },
    { reward: 320, entries: [{ type: "armored_orc", count: 8, interval: 700 }, { type: "wraith", count: 8, interval: 400 }] },
    { reward: 360, entries: [{ type: "troll", count: 5, interval: 1200 }, { type: "swarmling", count: 30, interval: 150 }] },
  ], 30, "plains", {
    type: "boss_warlord",
    escort: [
      { type: "armored_orc", count: 12, interval: 550 },
      { type: "orc", count: 18, interval: 350 },
      { type: "troll", count: 4, interval: 1000 }
    ]
  })
};

// -----------------------------------------------------------------------------
// MAP 2 - Stonebridge (river, two parallel paths)
// -----------------------------------------------------------------------------
const STONEBRIDGE: MapDef = {
  key: "stonebridge",
  name: "Stonebridge",
  biome: "river",
  // Two lanes converge at the bridge tile (10, 4), then a single combined
  // run heads south-east into the keep. The merge point is the natural
  // splash-tower hotspot; the two lead-in lanes are perfect for chokepoint
  // slows on either flank.
  waypoints: [
    // Northern lane -> bridge -> south leg into the castle.
    [-1, 2], [4, 2], [4, 4], [10, 4], [13, 4], [13, 7], [20, 7]
  ],
  paintedWaypoints: paintedPath(
    [-24, 58], [105, 64], [205, 92], [290, 132], [330, 162],
    [355, 185], [470, 184], [560, 191], [600, 220], [614, 300], [660, 342], [710, 354]
  ),
  altWaypoints: [
    // Southern lane joins the main path at the bridge.
    [-1, 7], [4, 7], [4, 4], [10, 4]
  ],
  altPaintedWaypoints: paintedPath(
    [-24, 356], [105, 350], [220, 322], [315, 270], [340, 238], [344, 202], [355, 185]
  ),
  pathBlockRadius: 52,
  blocked: [
    // Follow the river as it bends across the painted Stonebridge background.
    // A tile is blocked when a tower footprint would overlap visible water;
    // road/bridge cells are included harmlessly and remain path-blocked too.
    [19, 1],
    [17, 2], [18, 2], [19, 2],
    [0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3],
    [15, 3], [16, 3], [17, 3], [18, 3],
    [0, 4], [1, 4], [2, 4], [3, 4], [4, 4], [5, 4], [6, 4], [7, 4], [8, 4],
    [11, 4], [12, 4], [13, 4],
    [3, 5], [4, 5], [5, 5], [6, 5], [7, 5],
    [10, 5], [11, 5], [12, 5], [13, 5], [14, 5]
  ],
  decorations: [
    // Full row of water under the river strip. Bridge cells (10,5) and (13,5)
    // stay free because the painted bridges sit there.
    { c: 0, r: 5, kind: "water" }, { c: 1, r: 5, kind: "water" }, { c: 2, r: 5, kind: "water" },
    { c: 3, r: 5, kind: "water" }, { c: 5, r: 5, kind: "water" }, { c: 6, r: 5, kind: "water" },
    { c: 7, r: 5, kind: "water" }, { c: 8, r: 5, kind: "water" }, { c: 9, r: 5, kind: "water" },
    { c: 11, r: 5, kind: "water" }, { c: 12, r: 5, kind: "water" }, { c: 14, r: 5, kind: "water" },
    { c: 15, r: 5, kind: "water" }, { c: 16, r: 5, kind: "water" }, { c: 17, r: 5, kind: "water" },
    { c: 18, r: 5, kind: "water" }, { c: 19, r: 5, kind: "water" },
    // Bridge keep markers framing the convergence point.
    { c: 9, r: 3, kind: "banner" }, { c: 11, r: 3, kind: "banner" }, { c: 10, r: 3, kind: "torch" }
  ],
  castleHp: 20,
  startGold: 280,
  description: "Two paths converge. Watch both flanks.",
  bossWave: 29,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 60, entries: [{ type: "goblin", count: 12, interval: 500 }] },
    { reward: 80, entries: [{ type: "skeleton", count: 16, interval: 400 }] },
    { reward: 100, entries: [{ type: "orc", count: 8, interval: 700 }, { type: "goblin", count: 8, interval: 400 }] },
    { reward: 110, entries: [{ type: "swarmling", count: 30, interval: 160 }] },
    { reward: 130, entries: [{ type: "skeleton", count: 12, interval: 350 }, { type: "wraith", count: 4, interval: 700 }] },
    { reward: 150, entries: [{ type: "armored_orc", count: 6, interval: 800 }] },
    { reward: 170, entries: [{ type: "flame_imp", count: 10, interval: 450 }, { type: "goblin", count: 14, interval: 300 }] },
    { reward: 190, entries: [{ type: "wraith", count: 10, interval: 450 }] },
    { reward: 210, entries: [{ type: "troll", count: 2, interval: 1800 }, { type: "armored_orc", count: 6, interval: 700 }] },
    { reward: 240, entries: [{ type: "swarmling", count: 40, interval: 130 }, { type: "orc", count: 8, interval: 700 }] },
    { reward: 270, entries: [{ type: "skeleton", count: 30, interval: 220 }, { type: "wraith", count: 8, interval: 500 }] },
    { reward: 300, entries: [{ type: "troll", count: 4, interval: 1400 }, { type: "armored_orc", count: 8, interval: 600 }] },
    { reward: 330, entries: [{ type: "flame_imp", count: 14, interval: 350 }, { type: "armored_orc", count: 6, interval: 700 }] },
    { reward: 360, entries: [{ type: "troll", count: 5, interval: 1200 }, { type: "wraith", count: 12, interval: 350 }] },
    { reward: 400, entries: [{ type: "armored_orc", count: 12, interval: 500 }, { type: "swarmling", count: 30, interval: 150 }, { type: "cart", count: 2, interval: 2200 }] },
    { reward: 440, entries: [{ type: "troll", count: 6, interval: 1100 }, { type: "skeleton", count: 35, interval: 200 }] },
    { reward: 480, entries: [{ type: "wraith", count: 20, interval: 300 }, { type: "armored_orc", count: 10, interval: 500 }, { type: "cart", count: 3, interval: 1800 }] },
  ], 30, "river", {
    type: "boss_bridge_troll",
    escort: [
      { type: "troll", count: 5, interval: 900 },
      { type: "armored_orc", count: 14, interval: 480 }
    ]
  })
};

const FROSTFELL: MapDef = {
  key: "frostfell_gorge",
  name: "Frostfell Gorge",
  biome: "snow",
  // Long zig-zag with five direction changes. The vertical segments span the
  // full height so a single strong tower column covers two passes. Rewards
  // splash and slow towers placed at the hairpins.
  waypoints: [
    [-1, 4], [3, 4], [3, 1], [8, 1], [8, 7], [13, 7], [13, 2], [17, 2], [17, 5], [20, 5]
  ],
  paintedWaypoints: paintedPath(
    [-24, 258], [100, 258], [145, 242], [165, 210], [166, 126], [188, 99],
    [320, 98], [352, 116], [360, 282], [382, 314], [474, 315], [500, 290],
    [505, 168], [526, 140], [575, 135], [620, 154], [670, 147], [706, 112], [742, 92]
  ),
  pathBlockRadius: 54,
  blocked: [
    // Cliff ledges and open gorge cells called out in the painted map review.
    // These look like open snow in the grid but cannot physically support a tower.
    [0, 3], [1, 3],
    [7, 5],
    [6, 6], [15, 6],
    [5, 7], [6, 7], [16, 7],
    // Narrow ledge immediately below the castle on the right edge.
    [19, 2], [19, 3]
  ],
  decorations: [
    // Civilization markers; the snow biome auto-fills the rest with ice.
    { c: 6, r: 4, kind: "torch" }, { c: 11, r: 4, kind: "torch" }, { c: 15, r: 4, kind: "ice_shard" },
    { c: 17, r: 0, kind: "banner" }, { c: 18, r: 7, kind: "banner" }
  ],
  castleHp: 18,
  startGold: 320,
  description: "Blizzards weaken your towers. Stockpile heavy hitters.",
  bossWave: 34,
  modifiers: [
    { kind: "blizzard", everyMs: 60_000, durMs: 10_000, rofMult: 0.75 }
  ],
  waves: buildCampaignWaves([
    { reward: 80, entries: [{ type: "goblin", count: 16, interval: 400 }] },
    { reward: 100, entries: [{ type: "skeleton", count: 18, interval: 350 }] },
    { reward: 120, entries: [{ type: "wraith", count: 8, interval: 500 }, { type: "kobold_rider", count: 6, interval: 500 }] },
    { reward: 140, entries: [{ type: "swarmling", count: 35, interval: 140 }, { type: "skeleton", count: 8, interval: 400 }] },
    { reward: 160, entries: [{ type: "orc", count: 12, interval: 600 }, { type: "shieldman", count: 4, interval: 800 }] },
    { reward: 180, entries: [{ type: "wraith", count: 12, interval: 400 }, { type: "gargoyle", count: 4, interval: 800 }] },
    { reward: 200, entries: [{ type: "armored_orc", count: 8, interval: 700 }, { type: "ice_elemental", count: 3, interval: 1500 }] },
    { reward: 220, entries: [{ type: "flame_imp", count: 12, interval: 400 }, { type: "revenant", count: 4, interval: 900 }] },
    { reward: 240, entries: [{ type: "troll", count: 3, interval: 1600 }, { type: "wraith", count: 12, interval: 350 }] },
    { reward: 270, entries: [{ type: "armored_orc", count: 10, interval: 600 }, { type: "shadow_assassin", count: 6, interval: 700 }] },
    { reward: 300, entries: [{ type: "wraith", count: 18, interval: 320 }, { type: "gargoyle", count: 6, interval: 600 }] },
    { reward: 330, entries: [{ type: "troll", count: 5, interval: 1200 }, { type: "shieldman", count: 8, interval: 500 }] },
    { reward: 360, entries: [{ type: "flame_imp", count: 16, interval: 300 }, { type: "ice_elemental", count: 5, interval: 1000 }] },
    { reward: 400, entries: [{ type: "troll", count: 6, interval: 1100 }, { type: "swarmling", count: 40, interval: 120 }] },
    { reward: 440, entries: [{ type: "armored_orc", count: 14, interval: 450 }, { type: "revenant", count: 6, interval: 700 }] },
    { reward: 480, entries: [{ type: "siege_ram", count: 2, interval: 2500 }, { type: "skeleton", count: 40, interval: 180 }] },
    { reward: 520, entries: [{ type: "wraith", count: 24, interval: 280 }, { type: "gargoyle", count: 8, interval: 500 }] },
    { reward: 580, entries: [{ type: "flame_imp", count: 20, interval: 280 }, { type: "troll", count: 4, interval: 1300 }] },
    { reward: 640, entries: [{ type: "siege_ram", count: 3, interval: 2000 }, { type: "armored_orc", count: 16, interval: 400 }] },
  ], 35, "snow", {
    type: "boss_frost_wyrm",
    escort: [
      { type: "wraith", count: 18, interval: 320 },
      { type: "armored_orc", count: 12, interval: 480 },
      { type: "ice_elemental", count: 5, interval: 900 }
    ]
  })
};

// -----------------------------------------------------------------------------
// MAP 4 - Whispering Woods (plains, two-entry forest glade)
// Backed by a procedurally composited tile background (public/maps/whispering_woods.png).
// Two enemy lanes converge at the glade centre. Dense pine scatter makes
// effective tower placement rewarding; the central fountain marks the ideal
// splash hotspot.
// -----------------------------------------------------------------------------
const WHISPERING_WOODS: MapDef = {
  key: "whispering_woods",
  name: "Whispering Woods",
  biome: "plains",
  waypoints: [
    [-1, 2], [7, 2], [7, 5], [14, 5], [14, 7], [20, 7]
  ],
  paintedWaypoints: paintedPath(
    [-24, 86], [85, 96], [170, 135], [235, 168], [270, 183],
    [365, 177], [465, 172], [535, 192], [585, 246], [610, 305], [680, 350], [724, 366]
  ),
  altWaypoints: [
    [-1, 6], [7, 6], [7, 5]
  ],
  altPaintedWaypoints: paintedPath(
    [-24, 284], [85, 282], [170, 258], [230, 213], [270, 183]
  ),
  pathBlockRadius: 52,
  blocked: [
    [18, 6], [19, 6], [18, 7], [19, 7], [18, 8], [19, 8]
  ],
  decorations: [
    { c: 3, r: 0, kind: "pine" }, { c: 5, r: 0, kind: "pine" }, { c: 10, r: 0, kind: "pine" },
    { c: 12, r: 1, kind: "pine" }, { c: 16, r: 0, kind: "pine" },
    { c: 3, r: 8, kind: "pine" }, { c: 5, r: 8, kind: "pine" }, { c: 10, r: 8, kind: "pine" },
    { c: 16, r: 8, kind: "pine" }, { c: 9, r: 4, kind: "fountain" }
  ],
  castleHp: 20,
  startGold: 270,
  description: "Two paths thread the ancient wood. Cover both.",
  bossWave: 34,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 55,  entries: [{ type: "goblin", count: 10, interval: 600 }] },
    { reward: 70,  entries: [{ type: "goblin", count: 14, interval: 500 }, { type: "skeleton", count: 4, interval: 700 }] },
    { reward: 90,  entries: [{ type: "skeleton", count: 18, interval: 400 }] },
    { reward: 110, entries: [{ type: "orc", count: 8, interval: 750 }, { type: "goblin", count: 6, interval: 400 }] },
    { reward: 130, entries: [{ type: "swarmling", count: 28, interval: 170 }] },
    { reward: 150, entries: [{ type: "wraith", count: 6, interval: 550 }, { type: "goblin", count: 14, interval: 350 }] },
    { reward: 170, entries: [{ type: "flame_imp", count: 8, interval: 450 }, { type: "skeleton", count: 12, interval: 350 }] },
    { reward: 200, entries: [{ type: "armored_orc", count: 6, interval: 800 }] },
    { reward: 220, entries: [{ type: "orc", count: 12, interval: 550 }, { type: "wraith", count: 6, interval: 500 }] },
    { reward: 250, entries: [{ type: "troll", count: 2, interval: 1800 }, { type: "armored_orc", count: 6, interval: 700 }] },
    { reward: 290, entries: [{ type: "swarmling", count: 36, interval: 140 }, { type: "orc", count: 8, interval: 600 }] },
    { reward: 330, entries: [{ type: "troll", count: 4, interval: 1400 }, { type: "skeleton", count: 28, interval: 230 }] },
    { reward: 370, entries: [{ type: "armored_orc", count: 10, interval: 600 }, { type: "wraith", count: 8, interval: 450 }, { type: "elusive_stalker", count: 3, interval: 900 }] },
    { reward: 420, entries: [{ type: "troll", count: 5, interval: 1200 }, { type: "flame_imp", count: 12, interval: 350 }] },
    { reward: 480, entries: [{ type: "armored_orc", count: 8, interval: 550 }, { type: "swarmling", count: 32, interval: 150 }, { type: "elusive_stalker", count: 5, interval: 700 }] },
  ], 35, "plains", {
    type: "boss_warlord",
    escort: [
      { type: "armored_orc", count: 14, interval: 500 },
      { type: "swarmling", count: 35, interval: 140 },
      { type: "elusive_stalker", count: 6, interval: 700 }
    ]
  })
};

// -----------------------------------------------------------------------------
// MAP 5 - Dragon's Spine (mine, serpentine multi-bend path)
// A winding ore tunnel. The path loops back on itself three times, creating
// layered kill zones. Rock chokepoints force enemies into tight corridors.
// -----------------------------------------------------------------------------
const DRAGONS_SPINE: MapDef = {
  key: "dragons_spine",
  name: "Dragon's Spine",
  biome: "mine",
  waypoints: [
    [-1, 1], [4, 1], [4, 4], [1, 4], [1, 7], [8, 7], [8, 2], [13, 2], [13, 7], [16, 7], [16, 4], [20, 4]
  ],
  paintedWaypoints: paintedPath(
    [-24, 62], [82, 66], [127, 88], [143, 124], [139, 176], [112, 204],
    [75, 218], [58, 250], [66, 310], [92, 334], [330, 338], [390, 327],
    [414, 292], [416, 200], [438, 164], [535, 158], [570, 184], [583, 270],
    [610, 306], [704, 310]
  ),
  pathBlockRadius: 50,
  blocked: [
    [2, 0], [3, 0], [5, 0], [6, 0], [10, 0], [11, 0], [14, 0], [15, 0], [17, 0], [18, 0],
    [6, 4], [6, 5], [6, 6], [10, 4], [11, 4], [10, 5], [11, 5]
  ],
  decorations: [
    { c: 2, r: 0, kind: "rock" }, { c: 3, r: 0, kind: "rock" }, { c: 5, r: 0, kind: "rock" }, { c: 6, r: 0, kind: "rock" },
    { c: 10, r: 0, kind: "rock" }, { c: 11, r: 0, kind: "rock" }, { c: 14, r: 0, kind: "rock" }, { c: 15, r: 0, kind: "rock" },
    { c: 17, r: 0, kind: "rock" }, { c: 18, r: 0, kind: "rock" },
    { c: 6, r: 5, kind: "rock" }, { c: 10, r: 5, kind: "rock" }, { c: 11, r: 5, kind: "rock" },
    { c: 3, r: 5, kind: "torch" }, { c: 12, r: 6, kind: "torch" }, { c: 17, r: 5, kind: "banner" }
  ],
  castleHp: 18,
  startGold: 300,
  description: "The mine loops back on itself. Cover every bend.",
  bossWave: 34,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 70,  entries: [{ type: "goblin", count: 14, interval: 500 }] },
    { reward: 90,  entries: [{ type: "skeleton", count: 18, interval: 400 }, { type: "goblin", count: 6, interval: 500 }] },
    { reward: 110, entries: [{ type: "orc", count: 10, interval: 650 }] },
    { reward: 130, entries: [{ type: "swarmling", count: 32, interval: 150 }] },
    { reward: 150, entries: [{ type: "wraith", count: 8, interval: 500 }, { type: "skeleton", count: 12, interval: 350 }] },
    { reward: 170, entries: [{ type: "armored_orc", count: 7, interval: 750 }] },
    { reward: 200, entries: [{ type: "flame_imp", count: 10, interval: 400 }, { type: "orc", count: 10, interval: 550 }] },
    { reward: 230, entries: [{ type: "troll", count: 2, interval: 1700 }, { type: "armored_orc", count: 6, interval: 700 }] },
    { reward: 260, entries: [{ type: "wraith", count: 14, interval: 380 }, { type: "goblin", count: 18, interval: 300 }] },
    { reward: 300, entries: [{ type: "swarmling", count: 40, interval: 130 }, { type: "armored_orc", count: 8, interval: 600 }] },
    { reward: 340, entries: [{ type: "troll", count: 4, interval: 1400 }, { type: "skeleton", count: 30, interval: 220 }] },
    { reward: 380, entries: [{ type: "flame_imp", count: 14, interval: 350 }, { type: "wraith", count: 10, interval: 420 }] },
    { reward: 420, entries: [{ type: "armored_orc", count: 12, interval: 550 }, { type: "troll", count: 3, interval: 1500 }] },
    { reward: 470, entries: [{ type: "swarmling", count: 42, interval: 130 }, { type: "troll", count: 4, interval: 1200 }, { type: "hellhound", count: 4, interval: 800 }] },
    { reward: 530, entries: [{ type: "wraith", count: 20, interval: 300 }, { type: "armored_orc", count: 10, interval: 500 }] },
    { reward: 590, entries: [{ type: "troll", count: 6, interval: 1100 }, { type: "skeleton", count: 40, interval: 200 }] },
    { reward: 660, entries: [{ type: "armored_orc", count: 16, interval: 450 }, { type: "flame_imp", count: 16, interval: 320 }, { type: "hellhound", count: 7, interval: 620 }] },
  ], 35, "mine", {
    type: "boss_rock_wyrm",
    escort: [
      { type: "troll", count: 6, interval: 950 },
      { type: "armored_orc", count: 16, interval: 460 },
      { type: "hellhound", count: 8, interval: 600 }
    ]
  })
};

// -----------------------------------------------------------------------------
// MAP 6 - Sunken Marsh (swamp, 3-bend S-curve with water barriers)
// Muddy swamp path that loops vertically three times. Water cells ring the
// perimeter creating natural funnel points at each bend.
// -----------------------------------------------------------------------------
const SUNKEN_MARSH: MapDef = {
  key: "sunken_marsh",
  name: "Sunken Marsh",
  biome: "swamp",
  waypoints: [
    [-1, 4], [3, 4], [3, 1], [7, 1], [7, 7], [11, 7], [11, 2], [16, 2], [16, 5], [20, 5]
  ],
  paintedWaypoints: paintedPath(
    [-24, 224], [84, 220], [116, 199], [126, 157], [158, 124], [270, 118],
    [314, 137], [331, 190], [333, 272], [354, 309], [470, 310], [506, 286],
    [514, 196], [536, 158], [612, 150], [652, 160], [686, 142], [710, 107]
  ),
  pathBlockRadius: 52,
  blocked: [
    [5, 0], [9, 0], [13, 0], [18, 0],
    [5, 8], [9, 8], [14, 8], [18, 8],
    [1, 6], [1, 7], [1, 8],
    [19, 0], [19, 1], [19, 2]
  ],
  decorations: [
    { c: 5, r: 0, kind: "water" }, { c: 9, r: 0, kind: "water" }, { c: 13, r: 0, kind: "water" }, { c: 18, r: 0, kind: "water" },
    { c: 5, r: 8, kind: "water" }, { c: 9, r: 8, kind: "water" }, { c: 14, r: 8, kind: "water" }, { c: 18, r: 8, kind: "water" },
    { c: 1, r: 6, kind: "water" }, { c: 1, r: 7, kind: "water" }, { c: 1, r: 8, kind: "water" },
    { c: 19, r: 0, kind: "water" }, { c: 19, r: 1, kind: "water" }, { c: 19, r: 2, kind: "water" },
    { c: 4, r: 3, kind: "torch" }, { c: 12, r: 4, kind: "torch" }, { c: 17, r: 4, kind: "banner" }
  ],
  castleHp: 18,
  startGold: 310,
  description: "Mud slows you down too. Place slows before the bends.",
  bossWave: 39,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 75,  entries: [{ type: "goblin", count: 14, interval: 450 }] },
    { reward: 100, entries: [{ type: "skeleton", count: 20, interval: 380 }] },
    { reward: 120, entries: [{ type: "orc", count: 10, interval: 600 }, { type: "goblin", count: 8, interval: 400 }] },
    { reward: 145, entries: [{ type: "wraith", count: 8, interval: 500 }, { type: "swarmling", count: 20, interval: 200 }] },
    { reward: 170, entries: [{ type: "armored_orc", count: 7, interval: 750 }, { type: "skeleton", count: 12, interval: 380 }] },
    { reward: 195, entries: [{ type: "flame_imp", count: 10, interval: 420 }, { type: "orc", count: 8, interval: 600 }] },
    { reward: 225, entries: [{ type: "troll", count: 2, interval: 1700 }, { type: "wraith", count: 10, interval: 430 }] },
    { reward: 260, entries: [{ type: "swarmling", count: 38, interval: 135 }, { type: "armored_orc", count: 6, interval: 700 }] },
    { reward: 300, entries: [{ type: "wraith", count: 14, interval: 360 }, { type: "goblin", count: 20, interval: 280 }] },
    { reward: 340, entries: [{ type: "troll", count: 4, interval: 1400 }, { type: "skeleton", count: 32, interval: 210 }] },
    { reward: 380, entries: [{ type: "armored_orc", count: 12, interval: 540 }, { type: "flame_imp", count: 12, interval: 360 }] },
    { reward: 430, entries: [{ type: "troll", count: 5, interval: 1200 }, { type: "wraith", count: 14, interval: 340 }] },
    { reward: 490, entries: [{ type: "swarmling", count: 44, interval: 125 }, { type: "armored_orc", count: 10, interval: 520 }, { type: "bog_lurker", count: 4, interval: 950 }] },
    { reward: 550, entries: [{ type: "troll", count: 6, interval: 1100 }, { type: "skeleton", count: 42, interval: 190 }] },
    { reward: 620, entries: [{ type: "armored_orc", count: 16, interval: 450 }, { type: "wraith", count: 16, interval: 330 }] },
    { reward: 700, entries: [{ type: "flame_imp", count: 20, interval: 290 }, { type: "troll", count: 5, interval: 1200 }, { type: "plague_carrier", count: 3, interval: 1400 }] },
    { reward: 780, entries: [{ type: "swarmling", count: 50, interval: 120 }, { type: "troll", count: 6, interval: 1000 }, { type: "bog_lurker", count: 6, interval: 700 }, { type: "plague_carrier", count: 4, interval: 1200 }] },
  ], 40, "swamp", {
    type: "boss_bog_giant",
    escort: [
      { type: "troll", count: 7, interval: 900 },
      { type: "wraith", count: 18, interval: 340 },
      { type: "bog_lurker", count: 6, interval: 700 }
    ]
  })
};

// -----------------------------------------------------------------------------
// MAP 7 - Goldmere Coast (river, long dock road with water-blocked bottom edge)
// A straight coastal road that curves twice. The entire bottom row is water,
// cutting build space and funnelling enemies through the dock.
// -----------------------------------------------------------------------------
const GOLDMERE_COAST: MapDef = {
  key: "goldmere_coast",
  name: "Goldmere Coast",
  biome: "river",
  waypoints: [
    [-1, 1], [2, 1], [2, 7], [17, 7], [17, 4], [20, 4]
  ],
  paintedWaypoints: paintedPath(
    [-24, 68], [95, 61], [160, 67], [205, 92], [220, 132], [219, 238],
    [242, 272], [520, 278], [585, 272], [610, 244], [614, 170], [638, 127], [700, 78]
  ),
  pathBlockRadius: 52,
  blocked: [
    ...(Array.from({ length: 20 }, (_, c) => [c, 8] as [number, number])),
    [18, 3], [19, 3], [18, 4], [19, 4], [18, 5], [19, 5]
  ],
  decorations: [
    ...(Array.from({ length: 20 }, (_, c) => ({ c, r: 8, kind: "water" as const }))),
    { c: 5, r: 6, kind: "banner" }, { c: 10, r: 6, kind: "torch" }, { c: 15, r: 6, kind: "banner" },
    { c: 18, r: 4, kind: "rock" }, { c: 19, r: 4, kind: "rock" }
  ],
  castleHp: 18,
  startGold: 330,
  description: "The sea cuts your flank. Hold the dock road.",
  bossWave: 39,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 80,  entries: [{ type: "goblin", count: 16, interval: 430 }] },
    { reward: 105, entries: [{ type: "skeleton", count: 22, interval: 360 }] },
    { reward: 130, entries: [{ type: "orc", count: 12, interval: 580 }, { type: "goblin", count: 8, interval: 380 }] },
    { reward: 155, entries: [{ type: "swarmling", count: 34, interval: 145 }] },
    { reward: 180, entries: [{ type: "wraith", count: 10, interval: 480 }, { type: "skeleton", count: 14, interval: 360 }] },
    { reward: 210, entries: [{ type: "armored_orc", count: 8, interval: 730 }] },
    { reward: 240, entries: [{ type: "flame_imp", count: 12, interval: 400 }, { type: "orc", count: 10, interval: 560 }] },
    { reward: 275, entries: [{ type: "troll", count: 3, interval: 1600 }, { type: "armored_orc", count: 7, interval: 680 }] },
    { reward: 310, entries: [{ type: "wraith", count: 16, interval: 360 }, { type: "swarmling", count: 28, interval: 160 }] },
    { reward: 355, entries: [{ type: "swarmling", count: 44, interval: 125 }, { type: "armored_orc", count: 10, interval: 570 }] },
    { reward: 400, entries: [{ type: "troll", count: 5, interval: 1300 }, { type: "skeleton", count: 38, interval: 200 }] },
    { reward: 450, entries: [{ type: "flame_imp", count: 16, interval: 330 }, { type: "wraith", count: 12, interval: 390 }] },
    { reward: 510, entries: [{ type: "armored_orc", count: 14, interval: 520 }, { type: "troll", count: 4, interval: 1300 }] },
    { reward: 580, entries: [{ type: "swarmling", count: 48, interval: 120 }, { type: "troll", count: 5, interval: 1150 }, { type: "desert_nomad", count: 6, interval: 620 }] },
    { reward: 650, entries: [{ type: "wraith", count: 22, interval: 300 }, { type: "armored_orc", count: 12, interval: 500 }] },
    { reward: 730, entries: [{ type: "troll", count: 7, interval: 1050 }, { type: "skeleton", count: 44, interval: 190 }] },
    { reward: 820, entries: [{ type: "armored_orc", count: 18, interval: 430 }, { type: "flame_imp", count: 18, interval: 310 }, { type: "desert_nomad", count: 10, interval: 480 }] },
  ], 40, "river", {
    type: "boss_sea_leviathan",
    escort: [
      { type: "wraith", count: 20, interval: 320 },
      { type: "armored_orc", count: 16, interval: 440 },
      { type: "desert_nomad", count: 10, interval: 500 }
    ]
  })
};

// -----------------------------------------------------------------------------
// MAP 8 - Ironforge Approach (siege, L-path with rock chokepoints)
// A military approach road past a blocky fortification. The straight L-shape
// rewards tower columns; rock chokepoints compress the enemy column before
// the turn.
// -----------------------------------------------------------------------------
const IRONFORGE_APPROACH: MapDef = {
  key: "ironforge_app",
  name: "Ironforge Approach",
  biome: "siege",
  waypoints: [
    [-1, 4], [6, 4], [6, 2], [13, 2], [13, 6], [20, 6]
  ],
  paintedWaypoints: paintedPath(
    [-24, 232], [185, 232], [220, 218], [229, 174], [252, 153],
    [408, 153], [438, 170], [442, 218], [468, 235], [650, 235], [704, 198]
  ),
  pathBlockRadius: 50,
  blocked: [
    [8, 4], [9, 4], [10, 4], [11, 4],
    [8, 5], [9, 5], [10, 5], [11, 5],
    [18, 4], [19, 4], [18, 5], [19, 5], [18, 6], [19, 6], [18, 7], [19, 7]
  ],
  decorations: [
    { c: 8, r: 4, kind: "rock" }, { c: 9, r: 4, kind: "rock" }, { c: 10, r: 4, kind: "rock" }, { c: 11, r: 4, kind: "rock" },
    { c: 5, r: 1, kind: "banner" }, { c: 14, r: 1, kind: "banner" },
    { c: 10, r: 0, kind: "torch" }, { c: 10, r: 8, kind: "torch" }
  ],
  castleHp: 16,
  startGold: 350,
  description: "March to the fortress gate. The choke points are short.",
  bossWave: 39,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 90,  entries: [{ type: "goblin", count: 18, interval: 400 }] },
    { reward: 115, entries: [{ type: "skeleton", count: 24, interval: 340 }] },
    { reward: 140, entries: [{ type: "orc", count: 14, interval: 560 }, { type: "goblin", count: 10, interval: 360 }] },
    { reward: 165, entries: [{ type: "armored_orc", count: 9, interval: 720 }] },
    { reward: 195, entries: [{ type: "swarmling", count: 38, interval: 135 }, { type: "skeleton", count: 16, interval: 340 }] },
    { reward: 225, entries: [{ type: "wraith", count: 12, interval: 460 }, { type: "orc", count: 12, interval: 560 }] },
    { reward: 260, entries: [{ type: "flame_imp", count: 14, interval: 380 }, { type: "armored_orc", count: 8, interval: 700 }] },
    { reward: 300, entries: [{ type: "troll", count: 3, interval: 1550 }, { type: "wraith", count: 12, interval: 420 }] },
    { reward: 345, entries: [{ type: "armored_orc", count: 14, interval: 530 }, { type: "swarmling", count: 32, interval: 145 }] },
    { reward: 395, entries: [{ type: "troll", count: 5, interval: 1250 }, { type: "skeleton", count: 40, interval: 195 }] },
    { reward: 450, entries: [{ type: "wraith", count: 18, interval: 340 }, { type: "armored_orc", count: 12, interval: 510 }] },
    { reward: 510, entries: [{ type: "flame_imp", count: 18, interval: 310 }, { type: "troll", count: 4, interval: 1300 }] },
    { reward: 580, entries: [{ type: "swarmling", count: 50, interval: 115 }, { type: "armored_orc", count: 14, interval: 500 }] },
    { reward: 650, entries: [{ type: "troll", count: 7, interval: 1050 }, { type: "wraith", count: 18, interval: 330 }] },
    { reward: 730, entries: [{ type: "armored_orc", count: 20, interval: 420 }, { type: "skeleton", count: 46, interval: 185 }, { type: "golem_guardian", count: 1, interval: 3000 }] },
    { reward: 820, entries: [{ type: "flame_imp", count: 22, interval: 285 }, { type: "troll", count: 6, interval: 1100 }] },
    { reward: 920, entries: [{ type: "wraith", count: 26, interval: 295 }, { type: "armored_orc", count: 16, interval: 460 }] },
    { reward: 1000, entries: [{ type: "troll", count: 8, interval: 1000 }, { type: "swarmling", count: 55, interval: 110 }, { type: "golem_guardian", count: 2, interval: 2600 }] },
  ], 40, "siege", {
    type: "boss_siege_golem",
    escort: [
      { type: "armored_orc", count: 22, interval: 420 },
      { type: "troll", count: 7, interval: 950 },
      { type: "golem_guardian", count: 2, interval: 2500 }
    ]
  })
};

// -----------------------------------------------------------------------------
// MAP 9 — Twilight Chasm (siege, three-armed cross with alt bottom lane)
// The hardest map: two entry lanes and a switchback upper loop. Tower slots
// are scarce (rock corners), and the siege biome cuts ambient tint. Designed
// for veteran players.
// -----------------------------------------------------------------------------
const TWILIGHT_CHASM: MapDef = {
  key: "twilight_chasm",
  name: "Twilight Chasm",
  biome: "siege",
  waypoints: [
    [-1, 1], [5, 1], [5, 4], [14, 4], [14, 1], [17, 1], [17, 4], [20, 4]
  ],
  paintedWaypoints: paintedPath(
    [-24, 72], [94, 100], [190, 166], [244, 207], [270, 218],
    [405, 220], [487, 205], [508, 170], [505, 112], [530, 82],
    [620, 78], [650, 100], [647, 144], [670, 174], [710, 184]
  ),
  altWaypoints: [
    [-1, 7], [5, 7], [5, 4]
  ],
  altPaintedWaypoints: paintedPath(
    [-24, 342], [90, 316], [175, 270], [235, 230], [270, 218]
  ),
  pathBlockRadius: 58,
  blocked: [
    [10, 0], [11, 0], [10, 1], [11, 1],
    [10, 7], [11, 7], [10, 8], [11, 8],
    [18, 0], [19, 0], [18, 1], [19, 1],
    [18, 7], [19, 7], [18, 8], [19, 8]
  ],
  decorations: [
    { c: 10, r: 0, kind: "rock" }, { c: 11, r: 0, kind: "rock" }, { c: 10, r: 1, kind: "rock" }, { c: 11, r: 1, kind: "rock" },
    { c: 10, r: 7, kind: "rock" }, { c: 11, r: 7, kind: "rock" }, { c: 10, r: 8, kind: "rock" }, { c: 11, r: 8, kind: "rock" },
    { c: 3, r: 4, kind: "torch" }, { c: 8, r: 4, kind: "torch" }, { c: 15, r: 4, kind: "torch" },
    { c: 17, r: 0, kind: "banner" }, { c: 17, r: 8, kind: "banner" }
  ],
  castleHp: 15,
  startGold: 380,
  description: "Night falls on the chasm. Three fronts, no mercy.",
  bossWave: 49,
  modifiers: [],
  waves: buildCampaignWaves([
    { reward: 100,  entries: [{ type: "goblin", count: 20, interval: 380 }] },
    { reward: 130,  entries: [{ type: "skeleton", count: 26, interval: 320 }] },
    { reward: 160,  entries: [{ type: "orc", count: 16, interval: 530 }, { type: "goblin", count: 12, interval: 350 }] },
    { reward: 190,  entries: [{ type: "armored_orc", count: 10, interval: 700 }] },
    { reward: 225,  entries: [{ type: "swarmling", count: 42, interval: 130 }, { type: "wraith", count: 10, interval: 450 }] },
    { reward: 260,  entries: [{ type: "flame_imp", count: 14, interval: 370 }, { type: "skeleton", count: 20, interval: 320 }] },
    { reward: 300,  entries: [{ type: "troll", count: 3, interval: 1500 }, { type: "armored_orc", count: 10, interval: 660 }] },
    { reward: 345,  entries: [{ type: "wraith", count: 18, interval: 340 }, { type: "swarmling", count: 36, interval: 140 }] },
    { reward: 395,  entries: [{ type: "armored_orc", count: 16, interval: 510 }, { type: "troll", count: 4, interval: 1300 }] },
    { reward: 450,  entries: [{ type: "swarmling", count: 50, interval: 115 }, { type: "armored_orc", count: 14, interval: 490 }] },
    { reward: 510,  entries: [{ type: "troll", count: 6, interval: 1150 }, { type: "skeleton", count: 44, interval: 190 }] },
    { reward: 580,  entries: [{ type: "flame_imp", count: 20, interval: 285 }, { type: "wraith", count: 16, interval: 350 }] },
    { reward: 660,  entries: [{ type: "armored_orc", count: 20, interval: 420 }, { type: "troll", count: 5, interval: 1150 }] },
    { reward: 750,  entries: [{ type: "swarmling", count: 55, interval: 110 }, { type: "wraith", count: 20, interval: 310 }] },
    { reward: 850,  entries: [{ type: "troll", count: 8, interval: 1000 }, { type: "armored_orc", count: 18, interval: 430 }] },
    { reward: 950,  entries: [{ type: "flame_imp", count: 26, interval: 260 }, { type: "troll", count: 6, interval: 1080 }, { type: "void_specter", count: 3, interval: 1100 }] },
    { reward: 1060, entries: [{ type: "wraith", count: 30, interval: 280 }, { type: "armored_orc", count: 22, interval: 400 }] },
    { reward: 1180, entries: [{ type: "troll", count: 10, interval: 950 }, { type: "swarmling", count: 60, interval: 105 }] },
    { reward: 1320, entries: [{ type: "armored_orc", count: 24, interval: 400 }, { type: "wraith", count: 24, interval: 300 }, { type: "void_specter", count: 5, interval: 800 }] },
  ], 50, "siege", {
    type: "boss_shadow_titan",
    escort: [
      { type: "troll", count: 10, interval: 850 },
      { type: "armored_orc", count: 24, interval: 380 },
      { type: "wraith", count: 20, interval: 300 },
      { type: "void_specter", count: 6, interval: 750 }
    ]
  })
};

// Each map's wave script ends on its own boss. Waves past that point are
// Endless and get generated on demand by WaveManager, so nothing is padded here.
export const CAMPAIGN_MAPS: MapDef[] = [
  GREENWOLD,
  STONEBRIDGE,
  FROSTFELL,
  WHISPERING_WOODS,
  DRAGONS_SPINE,
  SUNKEN_MARSH,
  GOLDMERE_COAST,
  IRONFORGE_APPROACH,
  TWILIGHT_CHASM,
];

// `bossWave` duplicates the last authored wave index and nothing reads it yet.
// Assert the invariant here so it cannot drift unnoticed the way it would if it
// were only ever written.
if (import.meta.env?.DEV) {
  for (const map of CAMPAIGN_MAPS) {
    const lastIdx = map.waves.length - 1;
    console.assert(map.bossWave === lastIdx,
      `[maps] ${map.key}: bossWave ${map.bossWave} != last wave index ${lastIdx}`);
    console.assert(!!map.waves[lastIdx]?.boss,
      `[maps] ${map.key}: last authored wave is not flagged boss`);
  }
}

export function findMap(key: string): MapDef | undefined {
  return CAMPAIGN_MAPS.find(m => m.key === key);
}
